import { setRecentProduct } from '~/utils/products';

const store = {
    namespaced: true,
    state() {
        return {
            product: {},
            seo: {
                title: '',
                description: '',
                img: '',
                keywords: ''
            },
            banner: {
                show: true,
                showContent: false,
                data: null
            },
            breadcrumbs: [],
            availability: null,
            linkCopiedNotification: false,
            isAvailabilityFetching: false,
            similarProducts: [],
            compatibleProducts: [],
            recentProducts: [],
            isProductLoading: false
        };
    },
    getters: {
        getProduct(state) {
            return state.product;
        },
        getCompatibleProducts(state) {
            return state.compatibleProducts;
        },
        getSimilarProducts(state) {
            return state.similarProducts;
        },
        getSelectedColor(state) {
            if (state.product.colors) {
                return state.product.colors.find(e => e.url === state.product.url);
            }
        },
        getBanner(state) {
            return state.banner;
        },
        getBannerShowContent(state) {
            return state.banner.showContent;
        },
        getLinkCopiedNotification(state) {
            return state.linkCopiedNotification;
        },
        getAvailability(state) {
            return state.availability;
        },
        getIsAvailabilityFetching(state) {
            return state.isAvailabilityFetching;
        },
        getIsProductLoading(state) {
            return state.isProductLoading;
        },
        getSeoData(state) {
            return state.seo;
        },
        getRecentProducts(state) {
            return state.recentProducts;
        }
    },
    mutations: {
        setSelectedColor(state, colorId) {
            state.product.selectedColor = colorId;
        },
        setShowModalBanner(state, value) {
            state.banner.show = value;
        },
        setShowModalBannerContent(state, value) {
            state.banner.showContent = value;
        },
        setProduct(state, product) {
            state.product = product;
        },
        setBannerData(state, bannerData) {
            state.banner.data = bannerData;
        },
        setLinkCopiedNotification(state, value) {
            state.linkCopiedNotification = value;
        },
        setAvailability(state, availability) {
            state.availability = availability;
        },
        setIsAvailabilityFetching(state, value) {
            state.isAvailabilityFetching = value;
        },
        SET_SIMILAR_PRODUCTS(state, products) {
            state.similarProducts = products;
        },
        SET_COMPATIBLE_PRODUCTS(state, products) {
            state.compatibleProducts = products;
        },
        SET_RECENT_PRODUCTS(state, products) {
            state.recentProducts = products;
        },
        SET_PRODUCT_PROPERTIES(state, properties) {
            state.product.properties = properties;
        },
        SET_IS_PRODUCT_LOADING(state, value) {
            state.isProductLoading = value;
        },
        SET_SEO_DATA(state, seo) {
            state.seo = seo;
        }
    },
    actions: {
        async fetchProduct({ commit, dispatch, rootGetters }, { slug, route }) {
            const isFarmacie = rootGetters['global/getIsFarmacie'];
            commit('SET_IS_PRODUCT_LOADING', true);
            try {
                const product = await this.$axios.$get(`general/v2/product/${slug}/`);

                commit('setProduct', product.product);

                if (product.breadcrumbs && product.breadcrumbs.length > 0) {
                    let last = product.breadcrumbs.length - 1;
                    let categoryId = product.breadcrumbs[last].categoryId;

                    let banner = await this.$axios.get('general/banner-select/' + categoryId);
                    if (banner.data) {
                        commit('setBannerData', banner.data);
                    }

                    const currentProductPath = {
                        route: route.path,
                        title: product.product.title ?? product.product.code
                    };
                    commit(
                        'breadcrumbs/SET_BREADCRUMBS',
                        { breadcrumbs: product.breadcrumbs, currentProductPath },
                        { root: true }
                    );
                }
                await dispatch('fetchSeoData', { product: product.product, slug });
                await dispatch('fetchProductProperties', product.product.productId);
                if (!isFarmacie) {
                    await dispatch('fetchSimilarProducts', product.product.productId);
                } else {
                    commit('viewedProducts/addToViewedProducts', product.product, { root: true });

                    if (process.client) {
                        setRecentProduct(product.product.productId);
                    }

                    await dispatch('fetchProductRecents');
                }
            } catch (error) {
                try {
                    await dispatch('slugCheck', { slug });
                } catch {
                    console.error('Error fetching product:', error);
                }
            } finally {
                commit('SET_IS_PRODUCT_LOADING', false);
            }
        },
        async fetchSimilarProducts({ commit }, productId) {
            try {
                const { similarProducts, compatibleProducts } = await this.$axios.$get(
                    `/general/product_similar_products?product_id=${productId}`
                );
                commit('SET_SIMILAR_PRODUCTS', similarProducts);
                commit('SET_COMPATIBLE_PRODUCTS', compatibleProducts);
            } catch (error) {
                console.error('Error fetching similar products:', error);
            }
        },
        async fetchProductProperties({ commit }, productId) {
            try {
                const { properties } = await this.$axios.$get(`general/product_properties?product_id=${productId}`);
                commit('SET_PRODUCT_PROPERTIES', properties);
            } catch (error) {
                console.error('Error fetching product properties:', error);
            }
        },
        async slugCheck({ commit, rootGetters }, { slug }) {
            try {
                await this.$axios.$post(
                    'general/slug_check',
                    {
                        current_lang: this.$i18n.locales.filter(l => l.code !== this.$i18n.locale)[0].code,
                        new_lang: this.$i18n.locale,
                        current_slug: slug,
                        object_type: 'product'
                    },
                    {
                        redirectOnError: true
                    }
                );
            } catch {}
        },
        async fetchSeoData({ commit }, { product, slug }) {
            const description = product.seo_description;
            const title = product.seo_title;
            const keywords = product.seo_keywords;
            let seoData = {
                title: title || '',
                description: description || '',
                img: product.img || '',
                keywords: keywords || ''
            };

            if (!description || !title) {
                try {
                    const seo = await this.$axios.$get(`general/seo/article?product_slug=${slug}`);
                    if (seo) {
                        seoData = {
                            title: seo.title || '',
                            description: seo.description || '',
                            img: seo.img || '',
                            keywords: seo.keywords || ''
                        };
                    }
                } catch (error) {
                    console.error('Error fetching SEO data:', error);
                }
            }

            commit('SET_SEO_DATA', seoData);
        },
        async fetchAvailability({ commit }, productId) {
            commit('setIsAvailabilityFetching', true);
            try {
                const res = await this.$axios('/general/product-availability/', {
                    method: 'POST',
                    data: {
                        products: [productId]
                    }
                });
                commit('setAvailability', res.data);
            } catch (e) {
                console.error(e);
            } finally {
                commit('setIsAvailabilityFetching', false);
            }
        },
        async fetchProductRecents({ commit }) {
            if (process.client) {
                if (!localStorage?.getItem('recent_products_ecom')) {
                    return;
                }
                let recentProductsIds = JSON.parse(localStorage?.getItem('recent_products_ecom'));

                recentProductsIds = recentProductsIds.filter(id => id !== this.state.product.product.productId); // exclude current product from recents

                if (recentProductsIds?.length) {
                    const handledRecentProductsIds = recentProductsIds.join(',');

                    try {
                        const data = await this.$axios.$get(`/general/product-filter/?ids=${handledRecentProductsIds}`);

                        if (data) {
                            commit('SET_RECENT_PRODUCTS', data);
                        }
                    } catch (error) {
                        console.error('Failed to fetch sliders:', error);
                    }
                }
            }
        }
    }
};

export default store;
